import React from 'react';
import { TrendingUp, TrendingDown, Minus, Clock, AlertTriangle, Trophy, Target, Heart, Droplets } from 'lucide-react';
import * as Tooltip from '@radix-ui/react-tooltip';
import { HealthSummary, GlucoseMetrics, ExerciseMetrics, WeightMetrics } from '../../../lib/health/types';
import { cn } from '../../../lib/utils';
import { getThemeClass } from '../../../lib/theme';

// Enhanced trend icon components
const TrendIcon: React.FC<{ trend?: string; className?: string }> = ({ trend, className = "w-4 h-4" }) => {
  if (trend === 'up') return <TrendingUp className={`${className} text-red-500`} />;
  if (trend === 'down') return <TrendingDown className={`${className} text-green-500`} />;
  return <Minus className={`${className} text-gray-500`} />;
};

const ExerciseTrendIcon: React.FC<{ trend?: string; className?: string }> = ({ trend, className = "w-4 h-4" }) => {
  if (trend === 'up') return <TrendingUp className={`${className} text-green-500`} />;
  if (trend === 'down') return <TrendingDown className={`${className} text-red-500`} />;
  return <Minus className={`${className} text-gray-500`} />;
};

// Status indicator component
const StatusIndicator: React.FC<{ status: 'good' | 'warning' | 'danger' }> = ({ status }) => {
  const statusColors = {
    good: 'bg-green-500',
    warning: 'bg-yellow-500', 
    danger: 'bg-red-500'
  };
  
  return (
    <div className={cn(
      "absolute top-4 right-4 w-3 h-3 rounded-full",
      statusColors[status],
      "animate-pulse"
    )} />
  );
};

const calculateBMI = (weightLbs: number, heightInches: number = 71): number => {
  const weightKg = weightLbs * 0.453592;
  const heightM = heightInches * 0.0254;
  return weightKg / (heightM * heightM);
};

const getBMICategory = (bmi: number) => {
  if (bmi < 18.5) return { category: 'Underweight', color: 'text-blue-600', bgColor: 'bg-blue-50', emoji: '📏' };
  if (bmi < 25) return { category: 'Normal', color: 'text-green-600', bgColor: 'bg-green-50', emoji: '✅' };
  if (bmi < 30) return { category: 'Overweight', color: 'text-yellow-600', bgColor: 'bg-yellow-50', emoji: '⚠️' };
  return { category: 'Obese', color: 'text-red-600', bgColor: 'bg-red-50', emoji: '🚨' };
};

// Enhanced data chip component
const DataChip: React.FC<{ item: any; metricKey: string; isLatest: boolean }> = ({ 
  item, 
  metricKey, 
  isLatest 
}) => {
  const getValue = () => {
    if (metricKey === 'exercise') return item.minutes ?? '—';
    return item.value;
  };

  const getChipStyle = () => {
    const baseStyle = 'relative px-1.5 py-1 rounded text-xs font-medium border flex-shrink-0 min-w-[2rem] text-center transition-all duration-200';
    
    if (isLatest) {
      return `${baseStyle} bg-blue-600 text-white font-bold border-blue-600 shadow-sm`;
    }
    
    if (metricKey === 'exercise') {
      const minutes = item.minutes || 0;
      if (minutes > 0) return `${baseStyle} bg-green-50 text-green-700 border-green-200`;
      return `${baseStyle} bg-gray-50 text-gray-400 border-gray-200`;
    }
    
    // Glucose metrics
    if (item.critical) return `${baseStyle} bg-red-50 text-red-700 border-red-300 font-semibold`;
    if (item.outlier) return `${baseStyle} bg-yellow-50 text-yellow-700 border-yellow-300`;  
    if (item.inTarget) return `${baseStyle} bg-green-50 text-green-700 border-green-300`;
    
    return `${baseStyle} bg-gray-50 text-gray-700 border-gray-200`;
  };

  const value = getValue();
  const displayValue = value ?? '—';

  return (
    <Tooltip.Root>
      <Tooltip.Trigger asChild>
        <span className={getChipStyle()}>
          {displayValue}
          {item.notes && (
            <span className="absolute -top-1 -right-1 flex h-2 w-2 cursor-pointer transition-transform hover:scale-125">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-sky-500"></span>
            </span>
          )}
          {item.critical && <span className="ml-1 text-red-600">❗</span>}
          {item.outlier && !item.critical && <span className="ml-1 text-yellow-600">⚠️</span>}
        </span>
      </Tooltip.Trigger>
      {item.notes && (
        <Tooltip.Portal>
          <Tooltip.Content
            sideOffset={5}
            className="z-50 max-w-xs rounded-md bg-gray-900 px-3 py-2 text-sm text-white shadow-lg"
          >
            {item.notes}
            <Tooltip.Arrow className="fill-current text-gray-900" />
          </Tooltip.Content>
        </Tooltip.Portal>
      )}
    </Tooltip.Root>
  );
};

// Enhanced sparkline component
const Sparkline: React.FC<{ data: number[]; color?: string; width?: number; height?: number }> = ({ 
  data, 
  color = '#1976d2', 
  width = 80, 
  height = 28 
}) => {
  if (!data || data.length < 2) {
    return (
      <div 
        style={{ width, height }} 
        className="flex items-center justify-center text-gray-400 text-xs bg-gray-50 rounded"
      >
        No trend
      </div>
    );
  }
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value: number, index: number) => {
    const x = (index / (data.length - 1)) * (width - 8) + 4;
    const y = height - 4 - ((value - min) / range) * (height - 8);
    return `${x},${y}`;
  }).join(' ');

  const lastX = (data.length - 1) / (data.length - 1) * (width - 8) + 4;
  const lastY = height - 4 - ((data[data.length - 1] - min) / range) * (height - 8);

  return (
    <div className="relative">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="drop-shadow-sm">
        <defs>
          <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={color} stopOpacity="0.1"/>
            <stop offset="100%" stopColor={color} stopOpacity="0.3"/>
          </linearGradient>
        </defs>
        <path
          d={`M 4,${height-4} L ${points} L ${lastX},${height-4} Z`}
          fill={`url(#gradient-${color.replace('#', '')})`}
        />
        <polyline
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          points={points}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle
          cx={lastX}
          cy={lastY}
          r="3"
          fill={color}
          stroke="#fff"
          strokeWidth="2"
          className="drop-shadow-sm"
        />
      </svg>
    </div>
  );
};

// Helper functions for insights
const getGlucoseStatus = (average: number, target: number): 'good' | 'warning' | 'danger' => {
  if (average <= target) return 'good';
  if (average <= target * 1.2) return 'warning';
  return 'danger';
};

const getValueColorClass = (metricKey: string, data: any): string => {
  switch (metricKey) {
    case 'fasting':
      if (data.average <= 130) return 'text-green-600';
      if (data.average <= 150) return 'text-yellow-600';
      return 'text-red-600';
    case 'postMeal':
    case 'preMeal':
      if (data.average <= 180) return 'text-green-600';
      if (data.average <= 220) return 'text-yellow-600';
      return 'text-red-600';
    case 'weight':
      if (data.trend === 'down') return 'text-green-600';
      if (data.trend === 'up') return 'text-red-600';
      return 'text-blue-600';
    case 'exercise':
      return 'text-green-600';
    default:
      return 'text-gray-800';
  }
};

const getInsights = (metricKey: string, data: any, healthSummary: any) => {
  const insights = [];
  
  switch (metricKey) {
    case 'fasting':
      if (data.trend === 'down') insights.push({ icon: '📈', text: 'Trending down (good!)', type: 'positive' });
      if (data.average > 130 && data.outlierCount > 0) {
        insights.push({ icon: '🎯', text: `${data.outlierCount} readings above target`, type: 'neutral' });
      }
      break;
      
    case 'postMeal':
    case 'preMeal':
      if (data.criticalCount > 0) insights.push({ icon: '⚠️', text: `${data.criticalCount} critical reading(s)`, type: 'warning' });
      if (data.inTargetPct >= 75) insights.push({ icon: '✅', text: 'Excellent control', type: 'positive' });
      break;
      
    case 'weight':
      if (data.change && data.change < -5) insights.push({ icon: '🎯', text: 'Significant progress!', type: 'positive' });
      if (healthSummary?.exercise?.streak > 20) {
        insights.push({ icon: '💪', text: 'Exercise streak helping!', type: 'positive' });
      }
      break;
      
    case 'exercise':
      if (data.streak >= 20) insights.push({ icon: '🏆', text: 'Outstanding consistency!', type: 'celebration' });
      if (data.totalCalories) insights.push({ icon: '🔥', text: `${data.totalCalories} cal this week`, type: 'neutral' });
      break;
  }
  
  return insights;
};

// Enhanced metric configurations
const METRIC_CONFIGS = {
  fasting: {
    title: 'Fasting',
    titleIcon: Droplets,
    unit: 'mg/dL',
    target: 'Target: <130 mg/dL',
    accentColor: 'bg-amber-400',
    getMainValue: (data: any) => data?.average || 0,
    getSparklineData: (sparklineData: any) => sparklineData?.fasting || []
  },
  preMeal: {
    title: 'Pre-Meal',
    titleIcon: Droplets,
    unit: 'mg/dL', 
    target: 'Target: <140 mg/dL',
    accentColor: 'bg-cyan-500',
    getMainValue: (data: any) => data?.average || 0,
    getSparklineData: (sparklineData: any) => sparklineData?.preMeal || []
  },
  postMeal: {
    title: 'Post-Meal',
    titleIcon: Droplets,
    unit: 'mg/dL',
    target: 'Target: <180 mg/dL',
    accentColor: 'bg-violet-500',
    getMainValue: (data: any) => data?.average || 0,
    getSparklineData: (sparklineData: any) => sparklineData?.postMeal || []
  },
  weight: {
    title: 'Weight',
    unit: 'lbs',
    target: 'Goal: BMI 25.0 (180 lbs)',
    accentColor: 'bg-blue-500',
    getMainValue: (data: any) => parseFloat(data?.current?.toFixed(1)) || 0,
    getSparklineData: (sparklineData: any) => sparklineData?.weight || [],
    // BMI calculation with category
    getBMI: (data: any, heightInches: number = 71) => {
      const weight = parseFloat(data?.current?.toFixed(1)) || 0;
      if (weight <= 0) return null;
      const bmi = calculateBMI(weight, heightInches);
      const category = getBMICategory(bmi);
      return {
        value: bmi.toFixed(1),
        category: category.category,
        color: category.color,
        bgColor: category.bgColor,
        emoji: category.emoji
      };
    }
  },
  exercise: {
    title: 'Exercise',
    unit: 'min/day',
    target: 'Goal: 30+ min daily',
    accentColor: 'bg-green-500',
    getMainValue: (data: any, healthSummary: any) => {
      // Calculate average minutes per day based on active days
      if (!data?.totalMinutes || !data?.activeDays?.length) return 0;
      return Math.round(data.totalMinutes / data.activeDays.length);
    },
    getSparklineData: (data: any) => data?.last7Days?.map((d: any) => d.minutes).reverse() || []
  }
};

const SPARKLINE_COLORS: { [key: string]: string } = {
  fasting: '#facc15',
  preMeal: '#06b6d4', 
  postMeal: '#8b5cf6',
  weight: '#3b82f6',
  exercise: '#22c55e'
};

// Enhanced metric card component
const MetricCard: React.FC<{
  metricKey: string;
  data: any;
  config: any;
  sparklineData?: number[];
  healthSummary?: any;
}> = ({ metricKey, data, config, sparklineData = [], healthSummary }) => {
  if (!data) return null;

  const mainValue = config.getMainValue(data, healthSummary);
  const insights = getInsights(metricKey, data, healthSummary);
  const valueColorClass = getValueColorClass(metricKey, data);

  // Get BMI for weight metric
  const bmiData = metricKey === 'weight' && config.getBMI ? config.getBMI(data) : null;
  
  // Determine status for indicator
  let status: 'good' | 'warning' | 'danger' = 'good';
  if (metricKey === 'fasting' || metricKey === 'postMeal' || metricKey === 'preMeal') {
    const target = metricKey === 'fasting' ? 130 : metricKey === 'preMeal' ? 140 : 180;
    status = getGlucoseStatus(data.average, target);
  } else if (metricKey === 'exercise' && data.streak >= 20) {
    status = 'good';
  }
  
  // Celebration styling for achievements
  const isCelebration = (metricKey === 'exercise' && data.streak >= 20) || 
                       (metricKey === 'weight' && data.change && data.change < -5);

  return (
    <div className={cn(
      "relative bg-white rounded-2xl shadow-lg border border-gray-200/80 overflow-hidden h-full flex flex-col transition-all duration-300",
      "hover:shadow-xl hover:-translate-y-1",
      isCelebration && "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200"
    )}>
      {/* Status indicator */}
      <StatusIndicator status={status} />
      
      {/* Accent bar */}
      <div className={cn("h-2 w-full", config.accentColor)} />
      
      {/* Card content */}
      <div className="p-5 flex-1 flex flex-col">
        {/* Enhanced header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
            {config.title}
            {config.titleIcon && (
              <config.titleIcon className="w-4 h-4 text-red-500" />
            )}
          </h3>
            <div className="flex items-center space-x-3 flex-shrink-0">
              {metricKey === 'exercise' ? (
                <ExerciseTrendIcon trend={data.trend} className="w-5 h-5" />
              ) : (
                <TrendIcon trend={data.trend} className="w-5 h-5" />
              )}
              <Sparkline 
                data={sparklineData} 
                color={SPARKLINE_COLORS[metricKey]}
                width={80} 
                height={28} 
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-md">
            {config.target}
          </p>
        </div>

        {/* Enhanced main value */}
        <div className="mb-3">
          <div className="flex items-baseline justify-between">
            <div className="flex items-baseline">
              <span className={cn(
                "text-4xl sm:text-5xl font-extrabold leading-none",
                valueColorClass
              )}>
                {mainValue ?? '—'}
              </span>
              <span className="text-sm sm:text-base font-medium text-gray-500 ml-2">
                {config.unit}
              </span>
            </div>

            {/* BMI display for weight metric only - moved to the right */}
            {bmiData && (
              <div className="flex-shrink-0 ml-4">
                <div className={cn(
                  "flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium",
                  bmiData.bgColor,
                  bmiData.color
                )}>
                  <span className="text-sm">{bmiData.emoji}</span>
                  <div className="text-center">
                    <div className="font-bold">BMI {bmiData.value}</div>
                    <div className="text-[10px] opacity-75 -mt-0.5">{bmiData.category}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress bar for glucose metrics */}
        {(metricKey === 'fasting' || metricKey === 'postMeal' || metricKey === 'preMeal') && data.inTargetPct !== undefined && (
          <div className="mb-3">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-gray-600">Target</span>
              <span className="text-xs font-semibold text-gray-800">{data.inTargetPct}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div 
                className={cn(
                  "h-full rounded-full transition-all duration-1000 ease-out",
                  data.inTargetPct >= 75 ? "bg-gradient-to-r from-green-400 to-green-500" :
                  data.inTargetPct >= 50 ? "bg-gradient-to-r from-yellow-400 to-yellow-500" :
                  "bg-gradient-to-r from-red-400 to-red-500"
                )}
                style={{ width: `${Math.min(data.inTargetPct, 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Weight progress display */}
        {metricKey === 'weight' && data.change && (
          <div className="mb-3 p-2 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {data.trend === 'down' ? <TrendingDown className="w-4 h-4 text-green-600 mr-1" /> :
                 data.trend === 'up' ? <TrendingUp className="w-4 h-4 text-red-600 mr-1" /> :
                 <Minus className="w-4 h-4 text-gray-600 mr-1" />}
                <span className={cn(
                  "font-bold text-lg",
                  data.change < 0 ? "text-green-600" : data.change > 0 ? "text-red-600" : "text-gray-600"
                )}>
                  {data.change > 0 ? '+' : ''}{data.change.toFixed(1)} lbs
                </span>
              </div>
              <div className="text-xs text-blue-600 font-medium">
                {(data.current - 180).toFixed(1)} lbs away
              </div>
            </div>
          </div>
        )}

        {/* Exercise weekly stats */}
        {metricKey === 'exercise' && (data.averageHR || data.totalCalories) && (
          <div className="mb-3 p-2 bg-green-50 rounded-lg flex justify-around text-center">
            {data.averageHR && (
              <div>
                <div className="text-xs text-green-700 font-medium">Avg HR</div>
                <div className="font-bold text-green-800 text-lg">{Math.round(data.averageHR)} <span className="text-sm font-normal">bpm</span></div>
              </div>
            )}
            {data.totalCalories && (
              <div>
                <div className="text-xs text-green-700 font-medium">Total Calories</div>
                <div className="font-bold text-green-800 text-lg">{Math.round(data.totalCalories)} <span className="text-sm font-normal">kcal</span></div>
              </div>
            )}
          </div>
        )}

        {/* Exercise streak celebration */}
        {metricKey === 'exercise' && data.streak > 0 && (
          <div className={cn(
            "mb-3 p-2 rounded-lg text-center",
            data.streak >= 20 ? "bg-gradient-to-r from-yellow-100 to-orange-100" :
            data.streak >= 10 ? "bg-gradient-to-r from-green-100 to-emerald-100" :
            "bg-gray-50"
          )}>
            <div className="flex items-center justify-center space-x-1">
              <span className="text-lg">
                {data.streak >= 20 ? '🔥' : data.streak >= 10 ? '💪' : '✨'}
              </span>
              <span className={cn(
                "font-bold text-sm",
                data.streak >= 20 ? "text-orange-600" : "text-green-600"
              )}>
                {data.streak}-day streak
              </span>
            </div>
          </div>
        )}

        {/* Insights section */}
        {metricKey !== 'exercise' && insights.length > 0 && (
          <div className="mb-3 space-y-1">
            {insights.slice(0, 1).map((insight, index) => (
              <div key={index} className={cn(
                "flex items-center text-xs p-1.5 rounded",
                insight.type === 'positive' ? "bg-green-50 text-green-700" :
                insight.type === 'warning' ? "bg-yellow-50 text-yellow-700" :
                insight.type === 'celebration' ? "bg-blue-50 text-blue-700" :
                "bg-gray-50 text-gray-600"
              )}>
                <span className="mr-1.5 text-sm">{insight.icon}</span>
                <span className="font-medium">{insight.text}</span>
              </div>
            ))}
          </div>
        )}

        {/* Spacer */}
        <div className="flex-grow" />

        {/* Divider and chips */}
        <hr className="border-gray-200 mb-2" />
        <div className="flex gap-1.5 justify-center flex-wrap">
          {(() => {
            const chipData = metricKey === 'weight' 
              ? data.recent?.slice(-5).reverse().map((v: number) => ({ value: v.toFixed(1) })) || []
              : metricKey === 'exercise'
                ? data.last7Days?.map((d: any) => ({ minutes: d.minutes })) || []
                : data.recentValues || [];
            
            return chipData.slice(0, 5).map((item: any, index: number) => (
              <DataChip 
                key={index}
                item={item}
                metricKey={metricKey}
                isLatest={index === 0}
              />
            ));
          })()}
        </div>
      </div>
    </div>
  );
};

// Critical alerts component
const CriticalAlerts: React.FC<{ healthSummary: any }> = ({ healthSummary }) => {
  const alerts = [];
  
  // Check for critical readings
  if (healthSummary.postMeal?.criticalCount > 0) {
    alerts.push({
      type: 'critical',
      message: `Critical glucose reading detected (>250 mg/dL). Consider consulting healthcare provider.`,
      icon: <AlertTriangle className="w-5 h-5 text-red-600" />
    });
  }
  
  if (alerts.length === 0) return null;
  
  return (
    <div className="mb-6">
      {alerts.map((alert, index) => (
        <div key={index} className={cn(
          "flex items-center p-4 rounded-lg border-l-4",
          "bg-red-50 border-red-400 text-red-800"
        )}>
          {alert.icon}
          <span className="ml-3 font-medium">{alert.message}</span>
        </div>
      ))}
    </div>
  );
};

interface EnhancedHealthSummaryWidgetProps {
  healthSummary: HealthSummary | null;
  loading: boolean;
  error: string | null;
}

const EnhancedHealthSummaryWidget: React.FC<EnhancedHealthSummaryWidgetProps> = ({ healthSummary, loading, error }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-64 bg-slate-200 rounded-2xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-red-50 text-red-700 rounded-lg">
        <AlertTriangle className="w-12 h-12 mb-4" />
        <h3 className="text-xl font-semibold">Error Loading Data</h3>
        <p className="mt-2 text-center">{error}</p>
      </div>
    );
  }

  if (!healthSummary) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 text-gray-500 rounded-lg">
        <h3 className="text-xl font-semibold">No Data Available</h3>
        <p className="mt-2 text-center">Health summary could not be loaded.</p>
      </div>
    );
  }

  const metricOrder: (keyof HealthSummary)[] = ['fasting', 'postMeal', 'weight', 'exercise'];
  
  const availableMetrics = metricOrder.filter(key => {
    const metricData = healthSummary[key];
    if (!metricData) return false;

    switch (key) {
      case 'fasting':
      case 'postMeal': {
        const data = metricData as GlucoseMetrics;
        return data.count > 0;
      }
      case 'exercise': {
        const data = metricData as ExerciseMetrics;
        return data.sessionsCount > 0;
      }
      case 'weight': {
        const data = metricData as WeightMetrics;
        return data.current !== null;
      }
      default:
        return false;
    }
  });

  const lastUpdated = new Date(healthSummary.lastUpdated).toLocaleString(undefined, { 
    dateStyle: 'short', 
    timeStyle: 'short' 
  });

  return (
    <Tooltip.Provider delayDuration={100}>
      <div className="bg-slate-50/80 rounded-2xl py-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-end mb-6">
          <p className="text-xs text-gray-500 flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            Updated: {lastUpdated}
          </p>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
          {availableMetrics.map(metricKey => {
            const config = METRIC_CONFIGS[metricKey as keyof typeof METRIC_CONFIGS];
            if (!config) return null;
            
            const metricData = healthSummary[metricKey as keyof typeof healthSummary];
            
            const sparklineDataForMetric = metricKey === 'exercise'
              ? METRIC_CONFIGS.exercise.getSparklineData(metricData)
              : healthSummary.sparklineData?.[metricKey as keyof typeof healthSummary.sparklineData];

            return (
              <MetricCard 
                key={metricKey} 
                metricKey={metricKey} 
                data={metricData}
                config={config}
                sparklineData={sparklineDataForMetric}
                healthSummary={healthSummary}
              />
            );
          })}
        </div>

        {/* Footer with data period */}
        {healthSummary.period && (
          <div className="text-xs text-slate-400 text-right">
            Data period: {healthSummary.period}
          </div>
        )}
      </div>
    </Tooltip.Provider>
  );
};

export default EnhancedHealthSummaryWidget;